import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "@/hooks/useCompany";
import { MessageSquarePlus, Loader2, Paperclip, X, CheckCircle2 } from "lucide-react";
import analytics from "@/lib/segment";

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 10;

export const LeaveFeedbackCard = () => {
  const [issues, setIssues] = useState("");
  const [desiredFeatures, setDesiredFeatures] = useState("");
  const [otherComments, setOtherComments] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast } = useToast();
  const { company } = useCompany();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024);
    if (valid.length < selected.length) {
      toast({
        title: "File too large",
        description: `Files must be under ${MAX_FILE_SIZE_MB}MB each.`,
        variant: "destructive",
      });
    }
    setFiles(prev => [...prev, ...valid].slice(0, MAX_FILES));
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!issues.trim() && !desiredFeatures.trim() && !otherComments.trim() && files.length === 0) {
      toast({
        title: "Empty feedback",
        description: "Please fill in at least one field or attach a file.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You must be logged in.");

      // Upload attachments
      const attachmentUrls: string[] = [];
      for (const file of files) {
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const filePath = `${user.id}/${Date.now()}-${sanitizedName}`;
        const { error: uploadError } = await supabase.storage
          .from("feedback-attachments")
          .upload(filePath, file);
        if (uploadError) throw uploadError;
        attachmentUrls.push(filePath);
      }

      const { error } = await supabase.from("feedback_forms").insert({
        user_id: user.id,
        company_id: company?.id || null,
        issues: issues.trim() || null,
        desired_features: desiredFeatures.trim() || null,
        other_comments: otherComments.trim() || null,
        attachment_urls: attachmentUrls,
      });

      if (error) throw error;

      analytics.track("Feedback Submitted", {
        has_issues: !!issues.trim(),
        has_features: !!desiredFeatures.trim(),
        has_comments: !!otherComments.trim(),
        attachment_count: attachmentUrls.length,
      });

      toast({ title: "Thank you!", description: "Your feedback has been submitted." });
      setShowSuccess(true);
      setIssues("");
      setDesiredFeatures("");
      setOtherComments("");
      setFiles([]);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit feedback.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquarePlus className="h-5 w-5" />
          Leave Feedback
        </CardTitle>
        <CardDescription>
          Help us improve HostCaptain — all fields are optional
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showSuccess && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 dark:bg-green-950/30 dark:border-green-800 dark:text-green-300">
            <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Thank you for your feedback!</p>
              <p className="text-sm opacity-80">We appreciate you taking the time to help us improve HostCaptain.</p>
            </div>
            <button onClick={() => setShowSuccess(false)} className="ml-auto shrink-0 opacity-60 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="fb-issues">Issues while using HostCaptain</Label>
          <Textarea
            id="fb-issues"
            placeholder="Describe any problems you've encountered..."
            value={issues}
            onChange={e => setIssues(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="fb-features">Desired features</Label>
          <Textarea
            id="fb-features"
            placeholder="What features would you like to see?"
            value={desiredFeatures}
            onChange={e => setDesiredFeatures(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="fb-other">Other comments</Label>
          <Textarea
            id="fb-other"
            placeholder="Anything else you'd like to share..."
            value={otherComments}
            onChange={e => setOtherComments(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="space-y-2">
          <Label>Attachments</Label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={submitting || files.length >= MAX_FILES}
              onClick={() => document.getElementById("fb-file-input")?.click()}
            >
              <Paperclip className="mr-2 h-4 w-4" />
              Attach files
            </Button>
            <span className="text-xs text-muted-foreground">
              Up to {MAX_FILES} files, {MAX_FILE_SIZE_MB}MB each
            </span>
          </div>
          <Input
            id="fb-file-input"
            type="file"
            multiple
            className="hidden"
            accept="image/*,.pdf,.txt,.csv,.log"
            onChange={handleFileChange}
            disabled={submitting}
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {files.map((file, i) => (
                <div key={i} className="flex items-center gap-1 bg-muted rounded px-2 py-1 text-xs">
                  <span className="truncate max-w-[150px]">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-muted-foreground hover:text-destructive"
                    disabled={submitting}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button onClick={handleSubmit} disabled={submitting} className="mt-2">
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Submit Feedback"
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
